import React, { useEffect, useMemo, useState, useRef } from 'react';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { listarCatalogo, listarUsados, obtenerMayoresDiferencias, obtenerEvolucionSemanal } from './api/catalogo';
import { obtenerMiHistorialIA } from './api/historialIA';
import Sidebar from './Sidebar';
import './DashboardPM.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import {
    BarChart3, TrendingUp, TrendingDown, Bot, RefreshCw, FileDown,
    Activity, Package, AlertTriangle, Layers, Car, DollarSign,
    ChevronDown, X, History,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    Cell, LineChart, Line, CartesianGrid, ReferenceLine,
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { dataCache, CACHE_KEYS } from './useDataCache';

const functions = getFunctions();
const claudePMFn = httpsCallable(functions, 'consultarClaudePM', { timeout: 170000 });

const clpM = n => n ? `$${(n / 1_000_000).toFixed(1)}M` : '—';
const clpFmt = n => n ? `$${new Intl.NumberFormat('es-CL').format(Math.round(n))}` : '—';

const MARCA_COLORS = [
    '#2563eb', '#0891b2', '#7c3aed', '#059669', '#dc2626', '#d97706',
    '#db2777', '#65a30d', '#0369a1', '#9333ea', '#b45309', '#0f766e',
];

const toNum = v => typeof v === 'number' ? v : Number(String(v || '0').replace(/[^0-9]/g, '')) || 0;

// yyyy-mm-dd + N días (N negativo resta) — en UTC para no arrastrar
// desfases de huso horario entre fechas ya formateadas como string.
function sumarDias(fechaStr, dias) {
    const d = new Date(`${fechaStr}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + dias);
    return d.toISOString().slice(0, 10);
}

// ── Cálculos puros — reutilizados tanto por el dashboard en vivo (useMemo
// sobre el catálogo cargado) como por la exportación a PDF con fecha
// elegida (sobre un catálogo re-consultado para esa ventana de 7 días). ──
function calcularKpis(modelos, variaciones) {
    if (!modelos.length) return null;
    const marcasUnicas = new Set(modelos.map(m => m.marca)).size;
    const versionesUnicas = modelos.length;
    let sumPrecios = 0, countPrecios = 0;
    modelos.forEach(m => {
        const tipos = m.tiposprecio || [];
        const precios = m.precio || [];
        const idxLista = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
        const idx = idxLista >= 0 ? idxLista : 0;
        const p = toNum(precios[idx]);
        if (p > 0) { sumPrecios += p; countPrecios++; }
    });
    const precioPromedio = countPrecios ? sumPrecios / countPrecios : 0;
    const subidas = variaciones.filter(v => v.diferencia > 0).length;
    const bajadas = variaciones.filter(v => v.diferencia < 0).length;
    return { marcasUnicas, versionesUnicas, precioPromedio, subidas, bajadas, totalVariaciones: variaciones.length };
}

function calcularMovimientoMarcas(variaciones) {
    const map = new Map();
    variaciones.forEach(v => { map.set(v.marca, (map.get(v.marca) || 0) + 1); });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([marca, count]) => ({ marca, count }));
}

function calcularVersionesPorMarca(modelos) {
    const map = new Map();
    modelos.forEach(m => { if (m.marca) map.set(m.marca, (map.get(m.marca) || 0) + 1); });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15).map(([marca, count]) => ({ marca, count }));
}

function calcularDistribucionCategoria(modelos) {
    const map = new Map();
    modelos.forEach(m => {
        const cat = m.categoria || 'Sin categoría';
        if (!map.has(cat)) map.set(cat, { count: 0, precios: [] });
        const entry = map.get(cat);
        entry.count++;
        const tipos = m.tiposprecio || [];
        const precios = m.precio || [];
        const idxLista = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
        const p = toNum(precios[idxLista >= 0 ? idxLista : 0]);
        if (p > 0) entry.precios.push(p);
    });
    return [...map.entries()]
        .sort((a, b) => b[1].count - a[1].count).slice(0, 10)
        .map(([cat, { count, precios }]) => ({
            cat, count,
            precioMin: precios.length ? Math.min(...precios) : 0,
            precioMax: precios.length ? Math.max(...precios) : 0,
            precioAvg: precios.length ? precios.reduce((a, b) => a + b, 0) / precios.length : 0,
        }));
}

function calcularGaps(distribucionCategoria) {
    return distribucionCategoria
        .filter(c => c.precioMax > 0 && c.precioMin > 0)
        .map(c => { const gap = c.precioMax - c.precioMin; return { ...c, gap, gapPct: (gap / c.precioMin) * 100 }; })
        .sort((a, b) => b.gap - a.gap).slice(0, 8);
}

// Variaciones entre dos ventanas de catálogo (cada una ya deduplicada a un
// auto único por vehicle_version_id, más reciente dentro de la ventana) —
// mismo criterio de "mejor diferencia por auto" que usa el backend para el
// reporte semanal, aplicado acá a dos fechas arbitrarias para el PDF.
function calcularVariacionesEntreVentanas(actualAutos, anteriorAutos) {
    const anteriorPorId = new Map(anteriorAutos.map(a => [a.id, a]));
    const resultados = [];
    for (const a of actualAutos) {
        const ant = anteriorPorId.get(a.id);
        if (!ant) continue;
        const tiposA = a.tiposprecio || []; const preciosA = a.precio || [];
        const tiposB = ant.tiposprecio || []; const preciosB = ant.precio || [];
        let mejor = null;
        tiposA.forEach((tipo, i) => {
            const idxB = tiposB.indexOf(tipo);
            if (idxB < 0) return;
            const pA = toNum(preciosA[i]); const pB = toNum(preciosB[idxB]);
            if (!pA || !pB) return;
            const diferencia = pA - pB;
            if (diferencia === 0) return;
            const diferenciaAbs = Math.abs(diferencia);
            if (!mejor || diferenciaAbs > mejor.diferenciaAbs) {
                mejor = { marca: a.marca, model: a.model, modelDetail: a.modelDetail, tipoPrecio: tipo, precioActual: pA, precioAnterior: pB, diferencia, diferenciaAbs };
            }
        });
        if (mejor) resultados.push(mejor);
    }
    return resultados.sort((a, b) => b.diferenciaAbs - a.diferenciaAbs).slice(0, 50);
}

// ── Tooltip del gráfico de evolución ──────────────────────────
function EvoTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,.08)', fontSize: 13,
        }}>
            <p style={{ fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ margin: '2px 0', color: p.color || '#2563eb' }}>
                    <span style={{ fontWeight: 600 }}>{p.value?.toLocaleString('es-CL')}</span>
                    {' '}<span style={{ color: '#94a3b8', fontSize: 11 }}>modelos</span>
                    {p.name !== 'total' && <span style={{ color: '#94a3b8', fontSize: 11 }}> · {p.name}</span>}
                </p>
            ))}
        </div>
    );
}

// ── MultiSelect para el gráfico ───────────────────────────────
function ChartMultiSelect({ options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('click', h);
        return () => document.removeEventListener('click', h);
    }, []);

    const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
    const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
    const label = selected.length === 0 ? 'Todas las marcas'
        : selected.length === 1 ? selected[0]
            : `${selected.length} marcas`;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 12px', borderRadius: 8,
                    border: '1.5px solid #e2e8f0',
                    background: selected.length ? '#eff6ff' : '#fff',
                    color: selected.length ? '#2563eb' : '#374151',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}
            >
                {label}
                <ChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>

            {selected.length > 0 && (
                <button
                    onClick={() => onChange([])}
                    title="Limpiar selección"
                    style={{
                        position: 'absolute', right: -28, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4,
                    }}
                >
                    <X size={13} />
                </button>
            )}

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 200,
                    width: 220, maxHeight: 280, display: 'flex', flexDirection: 'column',
                }}>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar marca…"
                            style={{
                                width: '100%', border: '1px solid #e2e8f0', borderRadius: 6,
                                padding: '5px 8px', fontSize: 12, outline: 'none', fontFamily: 'inherit',
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 6, padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
                        <button onClick={() => onChange([...filtered])}
                            style={{ flex: 1, fontSize: 11, padding: '4px 0', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit' }}>
                            Todos
                        </button>
                        <button onClick={() => onChange([])}
                            style={{ flex: 1, fontSize: 11, padding: '4px 0', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit' }}>
                            Limpiar
                        </button>
                    </div>
                    <div style={{ overflowY: 'auto', padding: '4px 0' }}>
                        {filtered.map(opt => (
                            <label key={opt} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                                background: selected.includes(opt) ? '#eff6ff' : 'transparent',
                                color: selected.includes(opt) ? '#2563eb' : '#374151',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={selected.includes(opt)}
                                    onChange={() => toggle(opt)}
                                    style={{ accentColor: '#2563eb' }}
                                />
                                {opt}
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
//   GraficoEvolucionSemanal
//   Consulta independiente — trae el conteo de modelos únicos por
//   marca de las últimas N cargas (obtenerEvolucionSemanal, Neon).
// ════════════════════════════════════════════════════════════════
function GraficoEvolucionSemanal({ marcasDisponibles }) {
    const [marcasSel, setMarcasSel] = useState([]);
    const [cargas, setCargas] = useState([]); // [{ loadedAt, total, porMarca: { marca: n } }] — orden cronológico asc
    const [loadingChart, setLoadingChart] = useState(true);

    useEffect(() => {
        const cargar = async () => {
            setLoadingChart(true);
            try {
                const data = await dataCache.get('evo_semanal_16', () => obtenerEvolucionSemanal(16), { ttl: 3600 });
                setCargas(data);
            } catch (e) {
                console.error('Error cargando gráfico semanal:', e);
            } finally {
                setLoadingChart(false);
            }
        };
        cargar();
    }, []);

    // ── Construir datos para el gráfico ──────────────────────────
    const chartData = useMemo(() => {
        return cargas.map(c => {
            const label = new Date(c.loadedAt).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
            if (marcasSel.length === 0) {
                // Sin filtro → total de esa carga
                return { semana: label, total: c.total };
            } else {
                // Con filtro → sumar solo las marcas seleccionadas
                const obj = { semana: label };
                let sumFiltro = 0;
                marcasSel.forEach(m => {
                    const v = c.porMarca?.[m] || 0;
                    obj[m] = v;
                    sumFiltro += v;
                });
                obj.total = sumFiltro; // para la línea de referencia
                return obj;
            }
        });
    }, [cargas, marcasSel]);

    // ── KPIs rápidos ─────────────────────────────────────────────
    const kpiChart = useMemo(() => {
        const valores = chartData.map(d => d.total).filter(v => v > 0);
        if (!valores.length) return null;
        const ultimo = chartData[chartData.length - 1]?.total || 0;
        const anterior = chartData[chartData.length - 2]?.total || 0;
        const diff = ultimo - anterior;
        const diffPct = anterior ? ((diff / anterior) * 100).toFixed(1) : null;
        return {
            ultimo,
            anterior,
            diff,
            diffPct,
            max: Math.max(...valores),
            min: Math.min(...valores),
            promedio: Math.round(valores.reduce((a, b) => a + b, 0) / valores.length),
        };
    }, [chartData]);

    const multiMarca = marcasSel.length > 0;

    return (
        <div className="dpm-card" style={{ marginTop: 0 }}>
            {/* Header */}
            <div className="dpm-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
                <div className="dpm-card-title">
                    <BarChart3 size={14} style={{ color: '#2563eb' }} />
                    Evolución semanal de modelos en catálogo
                    <span className="dpm-section-sub">
                        modelos únicos por semana de carga · últimas {cargas.length} cargas
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
                    <ChartMultiSelect
                        options={marcasDisponibles}
                        selected={marcasSel}
                        onChange={setMarcasSel}
                    />
                </div>
            </div>

            {/* KPIs rápidos */}
            {!loadingChart && kpiChart && (
                <div style={{
                    display: 'flex', gap: 12, flexWrap: 'wrap',
                    padding: '0 20px 16px',
                }}>
                    {[
                        {
                            label: 'Última carga',
                            value: kpiChart.ultimo.toLocaleString('es-CL'),
                            delta: kpiChart.diffPct !== null
                                ? { v: kpiChart.diffPct, up: kpiChart.diff >= 0 }
                                : null,
                        },
                        { label: 'Carga anterior', value: kpiChart.anterior.toLocaleString('es-CL') },
                        { label: 'Máximo', value: kpiChart.max.toLocaleString('es-CL') },
                        { label: 'Mínimo', value: kpiChart.min.toLocaleString('es-CL') },
                        { label: 'Promedio', value: kpiChart.promedio.toLocaleString('es-CL') },
                    ].map((k, i) => (
                        <div key={i} style={{
                            background: '#f8fafc', borderRadius: 8,
                            padding: '10px 16px', flex: '1 1 100px', minWidth: 90,
                        }}>
                            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{k.label}</p>
                            <p style={{ margin: '2px 0 0', fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                                {k.value}
                            </p>
                            {k.delta && (
                                <p style={{
                                    margin: '2px 0 0', fontSize: 11, fontWeight: 600,
                                    color: k.delta.up ? '#16a34a' : '#dc2626',
                                }}>
                                    {k.delta.up ? '▲' : '▼'} {Math.abs(k.delta.v)}% vs semana anterior
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Gráfico */}
            <div style={{ padding: '0 20px 20px', height: 260 }}>
                {loadingChart ? (
                    <div className="dpm-skeleton" style={{ height: '100%', borderRadius: 8 }} />
                ) : chartData.length === 0 ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
                        Sin datos suficientes para el gráfico
                    </div>
                ) : !multiMarca ? (
                    /* ── Vista total: barras simples ── */
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }} barCategoryGap="28%">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<EvoTooltip />} cursor={{ fill: '#f8fafc' }} />
                            {kpiChart && (
                                <ReferenceLine
                                    y={kpiChart.promedio}
                                    stroke="#94a3b8"
                                    strokeDasharray="4 3"
                                    label={{ value: `prom. ${kpiChart.promedio.toLocaleString('es-CL')}`, position: 'insideTopRight', fontSize: 10, fill: '#94a3b8' }}
                                />
                            )}
                            <Bar dataKey="total" name="total" radius={[5, 5, 0, 0]} maxBarSize={52}>
                                {chartData.map((entry, i) => {
                                    const isLast = i === chartData.length - 1;
                                    const isMax = entry.total === kpiChart?.max;
                                    return (
                                        <Cell
                                            key={i}
                                            fill={isLast ? '#2563eb' : isMax ? '#0891b2' : '#bfdbfe'}
                                        />
                                    );
                                })}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    /* ── Vista por marca: líneas por marca ── */
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<EvoTooltip />} />
                            {marcasSel.map((marca, i) => (
                                <Line
                                    key={marca}
                                    type="monotone"
                                    dataKey={marca}
                                    name={marca}
                                    stroke={MARCA_COLORS[i % MARCA_COLORS.length]}
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: MARCA_COLORS[i % MARCA_COLORS.length] }}
                                    activeDot={{ r: 5 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Leyenda cuando hay marcas seleccionadas */}
            {multiMarca && !loadingChart && (
                <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 10,
                    padding: '0 20px 16px',
                }}>
                    {marcasSel.map((m, i) => (
                        <span key={m} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 12, color: '#374151',
                        }}>
                            <span style={{
                                width: 10, height: 3, borderRadius: 2,
                                background: MARCA_COLORS[i % MARCA_COLORS.length],
                                display: 'inline-block',
                            }} />
                            {m}
                        </span>
                    ))}
                </div>
            )}

            <p style={{ margin: '0 20px 14px', fontSize: 11, color: '#cbd5e1' }}>
                Cada punto representa el total de modelos únicos (deduplicados por marca + modelo + versión) en esa carga semanal.
            </p>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════
//   DashboardPM principal
// ════════════════════════════════════════════════════════════════
function DashboardPM({ embedded = false }) {
    const [loading, setLoading] = useState(true);
    const [ultimaFecha, setUltimaFecha] = useState(null);
    const [variaciones, setVariaciones] = useState([]);
    const [modelos, setModelos] = useState([]);
    const [usados, setUsados] = useState([]);

    const [aiLoading, setAiLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState('');
    const [aiFechaVista, setAiFechaVista] = useState(null); // fecha del informe guardado que se está viendo (null = recién generado)

    // Informes ejecutivos guardados — cada análisis se persiste solo (vía
    // registrarUsoIA, assistant "claude_pm") en chat_history; acá solo se
    // lista para poder volver a verlos sin regenerar.
    const [informesGuardados, setInformesGuardados] = useState([]);
    const [loadingHistorialIA, setLoadingHistorialIA] = useState(false);
    const [mostrarHistorialIA, setMostrarHistorialIA] = useState(false);

    const cargarHistorialIA = async () => {
        setLoadingHistorialIA(true);
        try {
            const historial = await obtenerMiHistorialIA('claude_pm');
            setInformesGuardados(historial || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingHistorialIA(false);
        }
    };

    useEffect(() => { cargarHistorialIA(); }, []);

    // Fecha del informe a descargar — el PDF se arma con los autos únicos
    // cargados en la ventana de 7 días que termina en esta fecha (no
    // necesariamente la misma ventana que está mostrando el dashboard).
    const [fechaInforme, setFechaInforme] = useState('');
    const [exportando, setExportando] = useState(false);

    useEffect(() => { cargarDatos(); }, []);

    useEffect(() => {
        if (ultimaFecha && !fechaInforme) setFechaInforme(ultimaFecha.toISOString().slice(0, 10));
    }, [ultimaFecha]); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarDatos = async () => {
        setLoading(true);
        setAiResponse('');
        setAiFechaVista(null);
        try {
            const catalogo = await dataCache.get(CACHE_KEYS.CATALOGO, () => listarCatalogo(), { ttl: 3600 });
            setUltimaFecha(catalogo.ultimaFecha ? new Date(`${catalogo.ultimaFecha}T12:00:00Z`) : null);
            setModelos(catalogo.autos);

            const varData = await dataCache.get(CACHE_KEYS.VARIACIONES, () => obtenerMayoresDiferencias(), { ttl: 3600 });
            setVariaciones(varData?.top || []);

            const usadosData = await dataCache.get('usados_muestra_v2', async () => {
                const { usados: filas } = await listarUsados({ limit: 2000 });
                return filas;
            }, { ttl: 7200 });
            setUsados(usadosData);

        } catch (e) {
            console.error('Error cargando dashboard:', e);
        } finally {
            setLoading(false);
        }
    };

    const kpis = useMemo(() => calcularKpis(modelos, variaciones), [modelos, variaciones]);
    const movimientoMarcas = useMemo(() => calcularMovimientoMarcas(variaciones), [variaciones]);
    const versionesPorMarca = useMemo(() => calcularVersionesPorMarca(modelos), [modelos]);
    const distribucionCategoria = useMemo(() => calcularDistribucionCategoria(modelos), [modelos]);
    const gaps = useMemo(() => calcularGaps(distribucionCategoria), [distribucionCategoria]);

    const usadosVsNuevos = useMemo(() => {
        const marcasConUsados = new Map();
        usados.forEach(u => {
            if (!u.marca) return;
            const precio = toNum(u.precio);
            if (precio === 0) return;
            if (!marcasConUsados.has(u.marca)) marcasConUsados.set(u.marca, []);
            marcasConUsados.get(u.marca).push(precio);
        });
        const nuevosPorMarca = new Map();
        modelos.forEach(m => {
            if (!m.marca) return;
            const tipos = m.tiposprecio || [];
            const precios = m.precio || [];
            const idxLista = tipos.findIndex(t => String(t).toLowerCase().includes('lista'));
            const p = toNum((precios[idxLista >= 0 ? idxLista : 0] || '0'));
            if (p === 0) return;
            if (!nuevosPorMarca.has(m.marca)) nuevosPorMarca.set(m.marca, []);
            nuevosPorMarca.get(m.marca).push(p);
        });
        const result = [];
        marcasConUsados.forEach((preciosU, marca) => {
            const preciosN = nuevosPorMarca.get(marca) || [];
            if (!preciosN.length) return;
            const avgU = preciosU.reduce((a, b) => a + b, 0) / preciosU.length;
            const avgN = preciosN.reduce((a, b) => a + b, 0) / preciosN.length;
            const depPct = ((avgN - avgU) / avgN) * 100;
            result.push({ marca, avgNuevo: avgN, avgUsado: avgU, depPct, countU: preciosU.length });
        });
        return result.filter(r => r.depPct > 0 && r.depPct < 80).sort((a, b) => a.depPct - b.depPct).slice(0, 10);
    }, [modelos, usados]);

    const fmtFechaHistorial = (createdAt) => {
        if (!createdAt) return '—';
        const d = new Date(createdAt);
        return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) + ' ' +
            d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    };

    const verInformeGuardado = (entrada) => {
        setAiResponse(entrada.response || '');
        setAiFechaVista(entrada.createdAt);
        setMostrarHistorialIA(false);
    };

    const generarAnalisisIA = async () => {
        setAiLoading(true);
        try {
            const resumen = [
                `Mercado actual: ${kpis?.marcasUnicas} marcas, ${kpis?.versionesUnicas} versiones.`,
                `Precio promedio lista: ${clpFmt(kpis?.precioPromedio)}.`,
                `Variaciones semanales: ${kpis?.subidas} subidas, ${kpis?.bajadas} bajadas.`,
                `Top categorías por volumen: ${distribucionCategoria.slice(0, 4).map(c => `${c.cat}(${c.count})`).join(', ')}.`,
                `Gap más grande: ${gaps[0]?.cat} entre ${clpFmt(gaps[0]?.precioMin)} y ${clpFmt(gaps[0]?.precioMax)}.`,
                `Marca con mejor retención de valor: ${usadosVsNuevos[0]?.marca} (${usadosVsNuevos[0]?.depPct?.toFixed(1)}% depreciación).`,
                `Marcas con más movimiento de precios esta semana: ${movimientoMarcas.slice(0, 3).map(m => m.marca).join(', ')}.`,
            ].join(' ');
            const prompt = `Soy PM automotriz. Analiza el estado del mercado chileno con estos datos: ${resumen}
Dame un análisis ejecutivo del mercado actual: ¿qué está pasando con los precios?, ¿qué marcas están siendo más agresivas?, 
¿qué segmentos tienen oportunidades?, y 3 recomendaciones estratégicas concretas para un PM esta semana.`;
            const result = await claudePMFn({ prompt, uid: auth.currentUser?.uid });
            setAiResponse(result.data?.response || '');
            setAiFechaVista(null);
            cargarHistorialIA();
        } catch (e) {
            console.error(e);
            setAiResponse('Error al generar el análisis.');
        } finally {
            setAiLoading(false);
        }
    };

    // ── Exportar informe en PDF ───────────────────────────────────
    // jsPDF con las fuentes estándar (Helvetica) sólo dibuja Latin-1/CP1252
    // — se normaliza puntuación tipográfica y se descarta lo que quede fuera
    // de ese rango (mismo patrón que Comparadormodelos.js).
    const sanitizarParaPDF = (texto) => String(texto ?? '')
        .normalize('NFC')
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        .replace(/•/g, '-')
        .replace(/…/g, '...')
        .replace(/[^\x00-\xFF]/g, '');

    const markdownAPlano = (md) => sanitizarParaPDF((md || '')
        .replace(/^#{1,6}\s*/gm, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/[*_`~]/g, '')
        .replace(/^>\s?/gm, '')
        .replace(/^[-*]{3,}$/gm, '')
        .replace(/^[-*]\s+/gm, '- '));

    const exportarPDF = async () => {
        if (loading) { toast.warning('Espera a que termine de cargar el dashboard.'); return; }
        if (!fechaInforme) { toast.warning('Elige una fecha para el informe.'); return; }

        setExportando(true);
        let datos;
        try {
            // Ventana del informe: 7 días terminando en la fecha elegida.
            // Ventana anterior (misma duración, inmediatamente antes) para
            // poder calcular variaciones de precio sobre esa fecha.
            const desde = sumarDias(fechaInforme, -6);
            const desdeAnterior = sumarDias(fechaInforme, -13);
            const hastaAnterior = sumarDias(fechaInforme, -7);

            const [actualResp, anteriorResp] = await Promise.all([
                listarCatalogo({ desde, hasta: fechaInforme }),
                listarCatalogo({ desde: desdeAnterior, hasta: hastaAnterior }),
            ]);
            const modelosInforme = actualResp.autos || [];
            if (!modelosInforme.length) {
                toast.warning('No hay autos cargados en esa ventana de 7 días.');
                return;
            }
            const variacionesInforme = calcularVariacionesEntreVentanas(modelosInforme, anteriorResp.autos || []);

            datos = {
                fechaEtiqueta: new Date(`${fechaInforme}T12:00:00Z`).toLocaleDateString('es-CL'),
                ventanaEtiqueta: `${new Date(`${desde}T12:00:00Z`).toLocaleDateString('es-CL')} al ${new Date(`${fechaInforme}T12:00:00Z`).toLocaleDateString('es-CL')}`,
                kpis: calcularKpis(modelosInforme, variacionesInforme),
                topSubidas: variacionesInforme.filter(v => v.diferencia > 0).slice(0, 8),
                topBajadas: variacionesInforme.filter(v => v.diferencia < 0).slice(0, 8),
                distribucionCategoria: calcularDistribucionCategoria(modelosInforme),
                movimientoMarcas: calcularMovimientoMarcas(variacionesInforme),
                // Usados no tiene historial por fecha (no queda snapshot por
                // publicación) — se usa la muestra vigente como referencia.
                usadosVsNuevos,
                // El análisis IA en pantalla se generó sobre los datos de HOY —
                // solo tiene sentido incluirlo si el informe es para hoy.
                incluirAnalisisIA: fechaInforme === (ultimaFecha ? ultimaFecha.toISOString().slice(0, 10) : ''),
            };
            datos.gaps = calcularGaps(datos.distribucionCategoria);
        } catch (e) {
            console.error('Error preparando el informe:', e);
            toast.error('No se pudo armar el informe para esa fecha.');
            setExportando(false);
            return;
        }
        setExportando(false);

        if (!datos.kpis) { toast.warning('No hay datos suficientes para esa fecha.'); return; }
        const { kpis, topSubidas, topBajadas, distribucionCategoria, gaps, movimientoMarcas, usadosVsNuevos: usadosVsNuevosInforme, incluirAnalisisIA } = datos;

        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const marginX = 40;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = 40;
        const s = sanitizarParaPDF;

        const saltoDePaginaSiNecesario = (alturaNecesaria) => {
            if (y + alturaNecesaria > pageHeight - 40) { doc.addPage(); y = 40; }
        };
        const tituloSeccion = (texto) => {
            saltoDePaginaSiNecesario(28);
            doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
            doc.text(s(texto), marginX, y);
            y += 18;
        };

        // Header
        doc.setFontSize(17); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
        doc.text('Informe de Inteligencia de Mercado - DoctorCar', marginX, y);
        y += 20;
        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(120, 120, 120);
        doc.text(s(`Generado el ${new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}`
            + ` · Datos al ${datos.fechaEtiqueta} · Ventana: ${datos.ventanaEtiqueta}`), marginX, y);
        y += 24;

        // KPIs
        tituloSeccion('Resumen general');
        autoTable(doc, {
            startY: y,
            head: [['Marcas activas', 'Versiones en catálogo', 'Precio promedio lista', 'Movimientos semanales']],
            body: [[
                String(kpis.marcasUnicas ?? '—'),
                String(kpis.versionesUnicas ?? '—'),
                kpis.precioPromedio ? s(clpFmt(kpis.precioPromedio)) : '—',
                `${kpis.totalVariaciones ?? 0} (${kpis.subidas} subidas / ${kpis.bajadas} bajadas)`,
            ]],
            styles: { fontSize: 9, cellPadding: 6 },
            headStyles: { fillColor: [37, 99, 235] },
            margin: { left: marginX, right: marginX },
        });
        y = doc.lastAutoTable.finalY + 24;

        // Mayores subidas
        if (topSubidas.length) {
            tituloSeccion('Mayores subidas de precio esta semana');
            autoTable(doc, {
                startY: y,
                head: [['Marca', 'Modelo', 'Versión', 'Tipo precio', 'Antes', 'Ahora', 'Diferencia']],
                body: topSubidas.map(v => [
                    s(v.marca), s(v.model), s(v.modelDetail), s(v.tipoPrecio),
                    s(clpFmt(v.precioAnterior)), s(clpFmt(v.precioActual)), s(`+${clpFmt(v.diferencia)}`),
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [22, 163, 74] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Mayores bajadas
        if (topBajadas.length) {
            tituloSeccion('Mayores bajadas de precio esta semana');
            autoTable(doc, {
                startY: y,
                head: [['Marca', 'Modelo', 'Versión', 'Tipo precio', 'Antes', 'Ahora', 'Diferencia']],
                body: topBajadas.map(v => [
                    s(v.marca), s(v.model), s(v.modelDetail), s(v.tipoPrecio),
                    s(clpFmt(v.precioAnterior)), s(clpFmt(v.precioActual)), s(clpFmt(v.diferencia)),
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [220, 38, 38] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Distribución por categoría
        if (distribucionCategoria.length) {
            tituloSeccion('Distribución por categoría');
            autoTable(doc, {
                startY: y,
                head: [['Categoría', 'Versiones', 'Precio mín.', 'Precio máx.', 'Precio promedio']],
                body: distribucionCategoria.map(c => [
                    s(c.cat), String(c.count), s(clpFmt(c.precioMin)), s(clpFmt(c.precioMax)), s(clpFmt(c.precioAvg)),
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [8, 145, 178] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Gaps de precio
        if (gaps.length) {
            tituloSeccion('Mayor dispersión de precio por categoría');
            autoTable(doc, {
                startY: y,
                head: [['Categoría', 'Precio mín.', 'Precio máx.', 'Gap', 'Gap %']],
                body: gaps.map(g => [
                    s(g.cat), s(clpFmt(g.precioMin)), s(clpFmt(g.precioMax)), s(clpFmt(g.gap)), `${g.gapPct.toFixed(0)}%`,
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [124, 58, 237] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Marcas con más movimiento
        if (movimientoMarcas.length) {
            tituloSeccion('Marcas con más movimientos de precio');
            autoTable(doc, {
                startY: y,
                head: [['Marca', 'Movimientos']],
                body: movimientoMarcas.map(m => [s(m.marca), String(m.count)]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [37, 99, 235] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Usados vs nuevos (depreciación) — sin historial por fecha, muestra vigente.
        if (usadosVsNuevosInforme.length) {
            tituloSeccion('Retención de valor: usados vs. nuevos por marca');
            autoTable(doc, {
                startY: y,
                head: [['Marca', 'Precio nuevo prom.', 'Precio usado prom.', 'Depreciación']],
                body: usadosVsNuevosInforme.map(u => [
                    s(u.marca), s(clpFmt(u.avgNuevo)), s(clpFmt(u.avgUsado)), `${u.depPct.toFixed(1)}%`,
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [5, 150, 105] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Análisis ejecutivo IA — solo tiene sentido si el informe es de hoy
        // (el análisis en pantalla se generó sobre los datos actuales).
        if (incluirAnalisisIA && aiResponse) {
            tituloSeccion('Análisis ejecutivo (IA)');
            doc.setFontSize(9.5); doc.setFont(undefined, 'normal'); doc.setTextColor(40, 40, 40);
            const lineas = doc.splitTextToSize(markdownAPlano(aiResponse), pageWidth - marginX * 2);
            lineas.forEach(linea => {
                saltoDePaginaSiNecesario(14);
                doc.text(linea, marginX, y);
                y += 13;
            });
        }

        doc.save(`informe-mercado-doctorcar-${fechaInforme}.pdf`);
    };

    const maxMovimiento = movimientoMarcas[0]?.count || 1;
    const maxVersiones = versionesPorMarca[0]?.count || 1;
    const topSubidas = variaciones.filter(v => v.diferencia > 0).slice(0, 8);
    const topBajadas = variaciones.filter(v => v.diferencia < 0).slice(0, 8);

    // Marcas disponibles para el gráfico (del catálogo actual)
    const marcasDisponibles = useMemo(() =>
        Array.from(new Set(modelos.map(m => m.marca).filter(Boolean))).sort(),
        [modelos]
    );

    const innerContent = (
        <div className="dpm-main">
            <div className="dpm-wrapper">

                {/* Header */}
                <div className="dpm-header">
                    <div>
                        <h1 className="dpm-header-title">Inteligencia de Mercado</h1>
                        <p className="dpm-header-sub">
                            Dashboard PM · Mercado automotriz chileno
                            {ultimaFecha && ` · Datos al ${ultimaFecha.toLocaleDateString('es-CL')}`}
                        </p>
                    </div>
                    <div className="dpm-header-actions">
                        {ultimaFecha && (
                            <div className="dpm-fecha-badge">
                                <span className="dpm-fecha-dot" />
                                {ultimaFecha.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                        )}
                        <button className="dpm-btn" onClick={() => {
                            dataCache.invalidate(CACHE_KEYS.CATALOGO);
                            dataCache.invalidate(CACHE_KEYS.VARIACIONES);
                            dataCache.invalidate('evo_semanal_16');
                            dataCache.invalidate('usados_muestra_v2');
                            cargarDatos();
                        }} disabled={loading}>
                            <RefreshCw size={14} style={{ animation: loading ? 'dpm-spin .8s linear infinite' : 'none' }} />
                            Actualizar
                        </button>
                        <input
                            type="date"
                            className="dpm-date-input"
                            value={fechaInforme}
                            max={ultimaFecha ? ultimaFecha.toISOString().slice(0, 10) : undefined}
                            onChange={e => setFechaInforme(e.target.value)}
                            title="Fecha del informe — se arma con los autos únicos cargados en los 7 días anteriores a esta fecha"
                        />
                        <button className="dpm-btn" onClick={exportarPDF} disabled={loading || exportando || !fechaInforme} title="Descargar este informe en PDF">
                            <FileDown size={14} /> {exportando ? 'Generando…' : 'Descargar PDF'}
                        </button>
                        {!aiResponse && !aiLoading && (
                            <button className="dpm-btn dpm-btn--ai" onClick={generarAnalisisIA} disabled={loading || aiLoading}>
                                <Bot size={14} /> Análisis ejecutivo IA
                            </button>
                        )}
                        {informesGuardados.length > 0 && (
                            <div style={{ position: 'relative' }}>
                                <button className="dpm-btn" onClick={() => setMostrarHistorialIA(v => !v)} title="Ver análisis generados anteriormente">
                                    <History size={14} /> Informes anteriores ({informesGuardados.length})
                                </button>
                                {mostrarHistorialIA && (
                                    <div className="dpm-historial-dropdown">
                                        <div className="dpm-historial-dropdown-header">
                                            Últimos {informesGuardados.length} informes
                                            <button className="dpm-historial-dropdown-close" onClick={() => setMostrarHistorialIA(false)}><X size={13} /></button>
                                        </div>
                                        {loadingHistorialIA ? (
                                            <div className="dpm-historial-item-empty">Cargando…</div>
                                        ) : (
                                            informesGuardados.map((h, i) => (
                                                <button key={i} className="dpm-historial-item" onClick={() => verInformeGuardado(h)}>
                                                    <span className="dpm-historial-item-fecha">{fmtFechaHistorial(h.createdAt)}</span>
                                                    <span className="dpm-historial-item-preview">{(h.response || '').replace(/[#*_`|>-]/g, ' ').trim().slice(0, 90)}…</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="dpm-kpi-grid">
                    {[
                        { label: 'Marcas activas', value: kpis?.marcasUnicas ?? '—', icon: <Car size={16} />, color: '#2563eb', bg: '#eff6ff', accent: '#2563eb' },
                        { label: 'Versiones en catálogo', value: kpis?.versionesUnicas ?? '—', icon: <Package size={16} />, color: '#0891b2', bg: '#e0f2fe', accent: '#0891b2' },
                        { label: 'Precio promedio lista', value: kpis?.precioPromedio ? clpM(kpis.precioPromedio) : '—', icon: <DollarSign size={16} />, color: '#059669', bg: '#f0fdf4', accent: '#059669' },
                        {
                            label: 'Movimientos semanales',
                            value: kpis?.totalVariaciones ?? '—',
                            icon: <Activity size={16} />,
                            color: '#7c3aed', bg: '#f5f3ff', accent: '#7c3aed',
                            delta: kpis ? { up: kpis.subidas, down: kpis.bajadas } : null,
                        },
                    ].map(({ label, value, icon, color, bg, accent, delta }) => (
                        <div key={label} className="dpm-kpi">
                            <div className="dpm-kpi-accent" style={{ background: accent }} />
                            <div className="dpm-kpi-icon" style={{ background: bg, color }}>
                                {loading ? <div className="dpm-skeleton" style={{ width: 20, height: 20 }} /> : icon}
                            </div>
                            {loading
                                ? <div className="dpm-skeleton" style={{ height: 28, width: '60%', marginBottom: 6 }} />
                                : <div className="dpm-kpi-value">{value}</div>
                            }
                            <div className="dpm-kpi-label">{label}</div>
                            {delta && !loading && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                    <span className="dpm-kpi-delta dpm-kpi-delta--up">▲ {delta.up}</span>
                                    <span className="dpm-kpi-delta dpm-kpi-delta--down">▼ {delta.down}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* IA Analysis */}
                {(aiResponse || aiLoading) && (
                    <div className="dpm-ai-card">
                        <div className="dpm-ai-header">
                            <div className="dpm-ai-title">
                                <Bot size={14} style={{ color: '#60a5fa' }} />
                                Análisis ejecutivo — Product Manager
                                {aiFechaVista && !aiLoading && (
                                    <span className="dpm-ai-saved-badge">informe guardado · {fmtFechaHistorial(aiFechaVista)}</span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="dpm-btn dpm-btn--ai" style={{ fontSize: 12, padding: '6px 12px' }} onClick={generarAnalisisIA} disabled={aiLoading}>
                                    <RefreshCw size={12} /> {aiFechaVista ? 'Generar uno nuevo' : 'Regenerar'}
                                </button>
                                <button className="dpm-btn" style={{ fontSize: 12, padding: '6px 12px', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', color: '#94a3b8' }} onClick={() => { setAiResponse(''); setAiFechaVista(null); }}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                        {aiLoading ? (
                            <div className="dpm-ai-thinking">
                                <div className="dpm-ai-dot" /><div className="dpm-ai-dot" /><div className="dpm-ai-dot" />
                                <span>Generando análisis ejecutivo del mercado…</span>
                            </div>
                        ) : (
                            <div className="dpm-ai-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{aiResponse}</ReactMarkdown></div>
                        )}
                    </div>
                )}

                {/* Variaciones */}
                <div className="dpm-grid-2">
                    <div className="dpm-card">
                        <div className="dpm-card-header">
                            <div className="dpm-card-title">
                                <TrendingUp size={14} style={{ color: '#16a34a' }} />
                                Mayores subidas <span className="dpm-section-sub">esta semana</span>
                            </div>
                        </div>
                        <div className="dpm-var-list">
                            {loading ? Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="dpm-var-item"><div className="dpm-skeleton" style={{ flex: 1, height: 32 }} /></div>
                            )) : topSubidas.length === 0
                                ? <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin subidas esta semana</div>
                                : topSubidas.map((v, i) => {
                                    const maxDiff = Math.max(...topSubidas.map(x => x.diferenciaAbs));
                                    return (
                                        <div key={i} className="dpm-var-item">
                                            <span className="dpm-var-rank">#{i + 1}</span>
                                            <div className="dpm-var-info">
                                                <div className="dpm-var-name">{v.marca} {v.model}</div>
                                                <div className="dpm-var-detail">{v.modelDetail} · {v.tipoPrecio}</div>
                                            </div>
                                            <div className="dpm-var-bar-wrap">
                                                <div className="dpm-var-bar" style={{ width: `${(v.diferenciaAbs / maxDiff) * 100}%`, background: '#16a34a' }} />
                                            </div>
                                            <span className="dpm-var-delta dpm-var-delta--up">+{clpM(v.diferencia)}</span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>

                    <div className="dpm-card">
                        <div className="dpm-card-header">
                            <div className="dpm-card-title">
                                <TrendingDown size={14} style={{ color: '#dc2626' }} />
                                Mayores bajadas <span className="dpm-section-sub">esta semana</span>
                            </div>
                        </div>
                        <div className="dpm-var-list">
                            {loading ? Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="dpm-var-item"><div className="dpm-skeleton" style={{ flex: 1, height: 32 }} /></div>
                            )) : topBajadas.length === 0
                                ? <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin bajadas esta semana</div>
                                : topBajadas.map((v, i) => {
                                    const maxDiff = Math.max(...topBajadas.map(x => x.diferenciaAbs));
                                    return (
                                        <div key={i} className="dpm-var-item">
                                            <span className="dpm-var-rank">#{i + 1}</span>
                                            <div className="dpm-var-info">
                                                <div className="dpm-var-name">{v.marca} {v.model}</div>
                                                <div className="dpm-var-detail">{v.modelDetail} · {v.tipoPrecio}</div>
                                            </div>
                                            <div className="dpm-var-bar-wrap">
                                                <div className="dpm-var-bar" style={{ width: `${(v.diferenciaAbs / maxDiff) * 100}%`, background: '#dc2626' }} />
                                            </div>
                                            <span className="dpm-var-delta dpm-var-delta--down">{clpM(v.diferencia)}</span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>

                {/* Marcas activas + Versiones por marca */}
                <div className="dpm-grid-2">
                    <div className="dpm-card">
                        <div className="dpm-card-header">
                            <div className="dpm-card-title">
                                <Activity size={14} style={{ color: '#7c3aed' }} />
                                Marcas más activas en precios
                            </div>
                        </div>
                        <div className="dpm-marca-list">
                            {loading ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="dpm-skeleton" style={{ height: 14, marginBottom: 10 }} />) :
                                movimientoMarcas.map(({ marca, count }, i) => (
                                    <div key={marca} className="dpm-marca-item">
                                        <span className="dpm-marca-label">{marca}</span>
                                        <div className="dpm-marca-bar-wrap">
                                            <div className="dpm-marca-bar" style={{ width: `${(count / maxMovimiento) * 100}%`, background: MARCA_COLORS[i % MARCA_COLORS.length] }} />
                                        </div>
                                        <span className="dpm-marca-count">{count}</span>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="dpm-card">
                        <div className="dpm-card-header">
                            <div className="dpm-card-title">
                                <Package size={14} style={{ color: '#0891b2' }} />
                                Versiones por marca <span className="dpm-section-sub">catálogo actual</span>
                            </div>
                        </div>
                        <div className="dpm-marca-list">
                            {loading ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="dpm-skeleton" style={{ height: 14, marginBottom: 10 }} />) :
                                versionesPorMarca.map(({ marca, count }, i) => (
                                    <div key={marca} className="dpm-marca-item">
                                        <span className="dpm-marca-label">{marca}</span>
                                        <div className="dpm-marca-bar-wrap">
                                            <div className="dpm-marca-bar" style={{ width: `${(count / maxVersiones) * 100}%`, background: MARCA_COLORS[i % MARCA_COLORS.length] + 'cc' }} />
                                        </div>
                                        <span className="dpm-marca-count">{count}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>

                {/* Gaps de precio */}
                <div className="dpm-card">
                    <div className="dpm-card-header">
                        <div className="dpm-card-title">
                            <AlertTriangle size={14} style={{ color: '#d97706' }} />
                            Gaps de precio por segmento
                            <span className="dpm-section-sub">diferencia entre min y max dentro de cada categoría</span>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="dpm-gaps-table">
                            <thead>
                                <tr>
                                    <th>Categoría</th><th>Modelos</th><th>Precio mínimo</th>
                                    <th>Precio máximo</th><th>Gap absoluto</th><th>Gap %</th><th>Oportunidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="dpm-skeleton" style={{ height: 14 }} /></td>)}</tr>
                                )) : gaps.map(g => {
                                    const nivel = g.gapPct > 150 ? 'large' : g.gapPct > 80 ? 'medium' : 'small';
                                    return (
                                        <tr key={g.cat}>
                                            <td style={{ fontWeight: 600, color: '#0f172a' }}>{g.cat}</td>
                                            <td>{g.count}</td>
                                            <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{clpM(g.precioMin)}</td>
                                            <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{clpM(g.precioMax)}</td>
                                            <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, fontWeight: 600 }}>{clpM(g.gap)}</td>
                                            <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{g.gapPct.toFixed(0)}%</td>
                                            <td><span className={`dpm-gap-badge dpm-gap-badge--${nivel}`}>{nivel === 'large' ? 'Alta' : nivel === 'medium' ? 'Media' : 'Baja'}</span></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Distribución por categoría */}
                <div className="dpm-card">
                    <div className="dpm-card-header">
                        <div className="dpm-card-title">
                            <Layers size={14} style={{ color: '#2563eb' }} />
                            Distribución del catálogo por categoría
                        </div>
                    </div>
                    <div style={{ padding: '16px 20px 20px', height: 260 }}>
                        {loading ? <div className="dpm-skeleton" style={{ height: '100%', borderRadius: 8 }} /> : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={distribucionCategoria} margin={{ top: 4, right: 16, left: 0, bottom: 40 }}>
                                    <XAxis dataKey="cat" tick={{ fontSize: 11, fill: '#94a3b8' }} angle={-35} textAnchor="end" interval={0} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} width={32} />
                                    <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} formatter={(v) => [v, 'Modelos']} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {distribucionCategoria.map((_, i) => <Cell key={i} fill={MARCA_COLORS[i % MARCA_COLORS.length]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Retención de valor */}
                <div className="dpm-card">
                    <div className="dpm-card-header">
                        <div className="dpm-card-title">
                            <DollarSign size={14} style={{ color: '#059669' }} />
                            Retención de valor por marca
                            <span className="dpm-section-sub">precio nuevo vs promedio usado · menor % = mejor retención</span>
                        </div>
                    </div>
                    {loading ? (
                        <div style={{ padding: 20 }}><div className="dpm-skeleton" style={{ height: 120, borderRadius: 8 }} /></div>
                    ) : usadosVsNuevos.length === 0 ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Sin datos suficientes para comparar nuevos vs usados.</div>
                    ) : (
                        <div className="dpm-uvn-grid">
                            {usadosVsNuevos.map((u, i) => (
                                <div key={u.marca} className="dpm-uvn-item">
                                    <div className="dpm-uvn-marca">
                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: MARCA_COLORS[i % MARCA_COLORS.length], marginRight: 6 }} />
                                        {u.marca}
                                    </div>
                                    <div className="dpm-uvn-row"><span className="dpm-uvn-tipo">Nuevo prom.</span><span className="dpm-uvn-precio">{clpM(u.avgNuevo)}</span></div>
                                    <div className="dpm-uvn-row"><span className="dpm-uvn-tipo">Usado prom.</span><span className="dpm-uvn-precio">{clpM(u.avgUsado)}</span></div>
                                    <div className="dpm-uvn-row"><span className="dpm-uvn-tipo">Publicaciones</span><span style={{ fontSize: 11, color: '#64748b' }}>{u.countU}</span></div>
                                    <div className="dpm-uvn-dep" style={{ color: u.depPct < 20 ? '#059669' : u.depPct < 40 ? '#d97706' : '#ef4444' }}>
                                        ▼ {u.depPct.toFixed(1)}% depreciación estimada
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Gráfico evolución semanal — siempre al final ── */}
                <GraficoEvolucionSemanal marcasDisponibles={marcasDisponibles} />

            </div>
        </div>
    );

    if (embedded) return innerContent;
    return (
        <div className="layout">
            <Sidebar />
            {innerContent}
        </div>
    );
}

export default DashboardPM;